package uz.mint.frontend.components.sections

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import uz.mint.frontend.PageGutter
import uz.mint.frontend.ShellStyle
import uz.mint.frontend.i18n.Locale
import uz.mint.frontend.i18n.LegalDocument
import uz.mint.frontend.i18n.stringsFor
import uz.mint.frontend.toSitePalette

/**
 * A legal document at its own address.
 *
 * These lived in a full-screen overlay, which meant they had no URL: search engines could not
 * index them, a customer could not be sent a link, and the cabinet applications for Click and
 * Payme — both of which ask where the terms are published — had nothing to point at. The overlay
 * still exists for the footer on other pages; this is the same content as a page.
 *
 * **The body stays Russian on every locale.** Only the title is translated, the same rule the
 * overlay follows: these are operative texts, and a machine translation of a refund clause can be
 * wrong in a way marketing copy cannot.
 */
@Composable
fun LegalPage(locale: Locale, title: String, document: LegalDocument, pathFor: (Locale) -> String) {
    val strings = stringsFor(locale)
    val palette = ColorMode.current.toSitePalette()

    Column(Modifier.fillMaxWidth()) {
        NavHeader(
            locale = locale,
            strings = strings,
            // The landing sections are not on this page, so its anchors have to reach back to it.
            anchorBase = locale.homePath,
            // Stay on the same document when the language changes, rather than going home.
            localePath = pathFor,
        )

        Div(
            ShellStyle.toModifier()
                .padding(top = clamp(32.px, 3.4.vw, 56.px), bottom = clamp(48.px, 5.vw, 84.px), leftRight = PageGutter)
                .toAttrs()
        ) {
            Column(Modifier.fillMaxWidth().maxWidth(46.em).gap(10.px)) {
                H1(
                    Modifier
                        .fontSize(clamp(28.px, 3.2.vw, 44.px))
                        .lineHeight(1.1)
                        .letterSpacing((-0.03).em)
                        .fontWeight(FontWeight.ExtraBold)
                        .toAttrs()
                ) { Text(title) }

                SpanText(
                    document.revision,
                    Modifier.fontSize(13.px).color(palette.ink2).opacity(0.7),
                )

                P(
                    Modifier
                        .fontSize(16.px).lineHeight(1.6).color(palette.ink2)
                        .margin(top = 14.px)
                        .toAttrs()
                ) { Text(document.intro) }

                document.sections.forEach { section ->
                    H2(
                        Modifier
                            .fontSize(18.px).fontWeight(FontWeight.Bold)
                            .margin(top = 26.px)
                            .toAttrs()
                    ) { Text(section.heading) }

                    // Paragraphs are separated by blank lines in the source, and several clauses
                    // rely on that: the refund section is three distinct rules, and running them
                    // together turns a readable document into a wall.
                    section.body.split("\n\n").forEach { paragraph ->
                        P(
                            Modifier
                                .fontSize(15.px).lineHeight(1.65).color(palette.ink2)
                                .margin(top = 8.px)
                                .whiteSpace(WhiteSpace.PreLine)
                                .toAttrs()
                        ) { Text(paragraph) }
                    }
                }
            }
        }

        Footer(locale = locale, strings = strings)
    }
}
